import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/design-system";
import { Feedback, useResource } from "@/components/workflow/shared";
import { errorMessage } from "@/lib/api";
import { danas } from "@/lib/dan";
import { hasProcurement, procurement, recipientLabel, type Order } from "@/lib/procurement";

export function whatsappUrl(phone: string | null | undefined, text: string) {
  const digits = phone?.replace(/\D/g, "") ?? "";
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

export function OrderWhatsApp({ order, disabled, onSaved }: { order: Order; disabled?: boolean; onSaved: (text: string) => void }) {
  const { user } = useAuth();
  const allowed = order.stanje === "odobreno" && !order.skupna_narudzba_id && order.mail_stanje !== "slanje" &&
    (hasProcurement(user, "update", "all") || order.narucitelj_id === user?.id && hasProcurement(user, "create"));
  return allowed ? <WhatsAppShare key={`${order.id}:${order.updated_at}`} order={order} disabled={disabled} onSaved={onSaved} /> : null;
}

function WhatsAppShare({ order, disabled, onSaved }: { order: Order; disabled?: boolean; onSaved: (text: string) => void }) {
  // Fetch before the click so native share and window.open retain user activation.
  const pdf = useResource(`whatsapp-pdf:${order.id}:${order.updated_at}`, useCallback(signal => procurement.pdf(order.id, signal), [order.id]));
  const [phase, setPhase] = useState<"ready" | "sharing" | "away" | "confirm">("ready");
  const [busy, setBusy] = useState(false), [error, setError] = useState(""), [recipient, setRecipient] = useState("");
  const lock = useRef(false), downloadUrls = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const snapshot = recipientLabel(order) !== "Nije upisan";
  useEffect(() => {
    const pending = downloadUrls.current;
    return () => pending.forEach((timer, url) => { clearTimeout(timer); URL.revokeObjectURL(url); });
  }, []);
  useEffect(() => {
    if (phase !== "away") return;
    const returned = () => { if (document.visibilityState === "visible") setPhase("confirm"); };
    window.addEventListener("focus", returned); document.addEventListener("visibilitychange", returned);
    return () => { window.removeEventListener("focus", returned); document.removeEventListener("visibilitychange", returned); };
  }, [phase]);
  async function share() {
    if (lock.current || !pdf.data || disabled) return;
    lock.current = true; setError(""); setPhase("sharing");
    const text = `Narudžbenica ${order.broj || "(pregled bez broja)"} · ${order.svrha}. U privitku je PDF narudžbe.`;
    try {
      const file = new File([pdf.data], `Narudzbenica-${order.broj || "pregled"}.pdf`, { type: "application/pdf" });
      const mobile = window.matchMedia?.("(max-width: 767px)").matches || window.matchMedia?.("(pointer: coarse)").matches;
      let canShare = false;
      try { canShare = !!mobile && !!navigator.share && !!navigator.canShare?.({ files: [file] }); } catch { /* Fall back to a PDF download. */ }
      if (canShare) {
        await navigator.share({ files: [file], text });
        setPhase("confirm");
      } else {
        const url = URL.createObjectURL(file), link = document.createElement("a");
        link.href = url; link.download = file.name; document.body.appendChild(link); link.click(); link.remove();
        downloadUrls.current.set(url, setTimeout(() => { URL.revokeObjectURL(url); downloadUrls.current.delete(url); }, 30_000));
        const opened = window.open(whatsappUrl(order.primatelj_telefon, text), "_blank");
        if (!opened) throw new Error("Preglednik je blokirao otvaranje WhatsAppa. Dopustite skočne prozore i pokušajte ponovno.");
        opened.opener = null;
        setPhase("away");
      }
    } catch (cause) {
      setPhase("ready");
      if (!(cause && typeof cause === "object" && "name" in cause && cause.name === "AbortError")) setError(errorMessage(cause, cause instanceof Error ? cause.message : "Dijeljenje PDF-a nije uspjelo."));
    } finally { lock.current = false; }
  }
  async function confirm() {
    if (lock.current || disabled) return;
    if (!snapshot && !recipient.trim()) { setError("Upišite kome ste poslali narudžbu."); return; }
    lock.current = true; setBusy(true); setError("");
    try {
      await procurement.action(order.id, "posalji", { nacin: "whatsapp", datum: danas(), biljeska: "Korisnik je potvrdio slanje WhatsAppom.", ...(!snapshot ? { kome: recipient.trim() } : {}) });
      setPhase("ready"); onSaved("Slanje WhatsAppom zabilježeno je prema vašoj potvrdi. Primitak poruke nije potvrđen.");
    } catch (cause) { setError(errorMessage(cause, "Potvrda slanja nije spremljena.")); }
    finally { lock.current = false; setBusy(false); }
  }
  return <div className="min-w-0 space-y-2 [overflow-wrap:anywhere]">
    <Button variant="secondary" disabled={disabled || pdf.loading || !pdf.data || phase !== "ready"} onClick={() => void share()}>Pošalji WhatsAppom</Button>
    {pdf.loading && <p role="status">Priprema PDF-a za dijeljenje…</p>}
    <Feedback error={phase !== "confirm" ? error || pdf.error : null} />{pdf.error && <Button variant="secondary" onClick={pdf.reload}>Ponovno pripremi PDF</Button>}
    <p className="text-sm">Dijelite PDF s uređaja. Ako se PDF preuzme, priložite ga poruci u WhatsAppu. Aplikacija ne potvrđuje primitak poruke.</p>
    {phase === "away" && <Button variant="secondary" onClick={() => setPhase("confirm")}>Vratio/la sam se iz WhatsAppa</Button>}
    {phase === "confirm" && <Dialog title="Je li narudžba poslana WhatsAppom?" onClose={() => { if (!busy) { setPhase("ready"); setError(""); } }}><div className="avm-procurement space-y-4"><Feedback error={error} /><p>Potvrdite samo ako ste poslali poruku s PDF-om narudžbe. Primitak poruke nije potvrđen.</p>{!snapshot && <label>Kome ste poslali narudžbu<input required maxLength={2000} value={recipient} onChange={event => setRecipient(event.target.value)} /></label>}<div className="flex flex-wrap gap-2"><Button disabled={busy || disabled} onClick={() => void confirm()}>Da</Button><Button disabled={busy} variant="secondary" onClick={() => { setPhase("ready"); setError(""); }}>Nije</Button></div></div></Dialog>}
  </div>;
}
